<script setup lang="ts">
import MyMenusIcon from '@/assets/icons/Menus/MyMenusIcon.vue';
import TagIcon from '@/assets/icons/Menus/TagIcon.vue';
import GeneratedTitlesIcon from '@/assets/icons/Planning/GeneratedTitlesIcon.vue';
import PlanningIcon from '@/assets/icons/Planning/PlanningIcon.vue';
import HistoryIcon from '@/assets/icons/Profile/HistoryIcon.vue';
import ScanIcon from '@/assets/icons/Profile/ScanIcon.vue';
import MyRecipesMenuItem from '@/components/MyRecipes/MyRecipesMenuItem.vue';
import { useOnboarding } from '@/composables/useOnboarding';
import { usePermissions } from '@/composables/usePermissions';
import type { MessageSchema } from '@/i18n';
import { useDishesStore } from '@/stores/dishesStore';
import { useUserStore } from '@/stores/useUserStore';
import { useToast } from 'primevue/usetoast';
import { computed, markRaw, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const { can } = usePermissions();

const toast = useToast();
const user = ref(useUserStore().user);
const dishesStore = useDishesStore();
const restaurantId = user.value?.restaurants[0]?.id || null;

// Define all possible links with their permission requirements
const allLinks = [
  {
    name: t('homeView.myRecipes.history'),
    icon: markRaw(HistoryIcon),
    url: '/my-recipes/generation-history',
    className: 'onboarding-history-highlight',
    requiredPermissions: [
      { category: 'dishes' as const, action: 'view' },
      { category: 'dishes' as const, action: 'create' }, // History shows generated dishes
    ],
    requiresAny: true, // User needs at least one of these permissions
  },
  {
    name: t('homeView.myRecipes.myCategories'),
    icon: markRaw(TagIcon),
    url: '/my-recipes/tags',
    className: 'onboarding-folder-highlight',
    requiredPermissions: [
      { category: 'dishes' as const, action: 'view' },
      { category: 'dishes' as const, action: 'update' }, // Tags require update permission
    ],
    requiresAny: false, // User needs all these permissions
  },
  {
    name: t('homeView.myRecipes.scan'),
    icon: markRaw(ScanIcon),
    url: '/my-recipes/my-scans',
    requiredPermissions: [
      { category: 'dishes' as const, action: 'view' },
      { category: 'dishes' as const, action: 'create' }, // Scanning creates dishes
    ],
    requiresAny: false,
  },
  {
    name: t('homeView.myRecipes.planned'),
    icon: markRaw(PlanningIcon),
    url: '/my-recipes/planned',
    requiredPermissions: [],
    requiresAny: false,
  },
  {
    name: t('homeView.myRecipes.suggestions'),
    icon: markRaw(GeneratedTitlesIcon),
    url: '/my-recipes/titles',
    requiredPermissions: [
      { category: 'dishes' as const, action: 'view' },
      { category: 'dishes' as const, action: 'create' }, // Suggestions are for creating dishes
    ],
    requiresAny: true,
  },
  {
    name: t('homeView.myRecipes.myMenus'),
    icon: markRaw(MyMenusIcon),
    url: '/my-recipes/my-menus',
    requiredPermissions: [],
    requiresAny: false,
  },
];

// Filter links based on user permissions
const links = computed(() => {
  return allLinks.filter((link) => {
    if (link.requiresAny) {
      // User needs at least one of the required permissions
      return link.requiredPermissions.some((perm) => can(perm.category, perm.action));
    } else {
      // User needs all the required permissions
      return link.requiredPermissions.every((perm) => can(perm.category, perm.action));
    }
  });
});

onMounted(async () => {
  // Ne pas charger les tags de la DB si l'onboarding est actif
  const { isOnboardingActive } = useOnboarding();
  if (!isOnboardingActive.value) {
    await dishesStore.fetchTags(restaurantId, toast);
  }
});
</script>

<template>
  <main>
    <div class="mt-4">
      <MyRecipesMenuItem
        v-for="(link, index) in links"
        :key="link.name"
        :name="link.name"
        :icon="link.icon"
        :url="link.url"
        :index="index"
        :class="link.className"
      />
    </div>
  </main>
</template>
